import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'

import { MOBILE_VIEWPORT } from '../../guided-toolbox/utils/breakpoints'
import DesktopTabs from './DesktopTabs'
import MobileTabs from './MobileTabs'

/**
 * Normalize the accounts prop into `{ id, name }` objects. Callers may pass
 * either plain strings or objects; downstream code keys off a stable `id`.
 */
const normalize = accounts =>
  (accounts || []).map((account, i) =>
    typeof account === 'string'
      ? { id: String(i), name: account }
      : { id: account.id != null ? String(account.id) : account.name, name: account.name }
  )

/**
 * Account tab interface for the step-by-step advice flow's investments step.
 *
 * Chooses its presentation responsively based on the viewport width:
 *  - desktop: a row of tabs; long names truncate with an ellipsis, and when
 *    more accounts exist than fit in one row the last tab collapses to a
 *    "+N" control that opens a menu of the hidden accounts. Picking one
 *    promotes it into the right-most visible slot and makes it active.
 *  - mobile:  a single horizontally scrollable row; the active tab's label is
 *    shown in the client's brand color and long names truncate with an ellipsis.
 *
 * Controlled: the parent owns the active account via `activeAccount` /
 * `onAccountChange`.
 */
const AdviceInvestmentTabs = props => {
  const { accounts, activeAccount, onAccountChange, maxVisibleTabs, className } = props
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)

  const normalized = normalize(accounts)
  // Resolve the active account to an id so string / object callers behave the
  // same. Fall back to the first account when the given value doesn't match.
  const activeId =
    normalized.find(a => a.id === activeAccount || a.name === activeAccount)?.id ??
    normalized[0]?.id

  const handleChange = account => onAccountChange?.(account.name, account)

  if (normalized.length === 0) {
    return null
  }

  const shared = { accounts: normalized, activeId, onChange: handleChange, className }

  return isMobile ? (
    <MobileTabs {...shared} />
  ) : (
    <DesktopTabs {...shared} maxVisibleTabs={maxVisibleTabs} />
  )
}

AdviceInvestmentTabs.propTypes = {
  /** Accounts to show as tabs. Strings or `{ id?, name }` objects. */
  accounts: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string.isRequired,
      }),
    ])
  ).isRequired,
  /** The active account, matched by id or name. Defaults to the first account. */
  activeAccount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Called with `(name, account)` when the active account changes. */
  onAccountChange: PropTypes.func,
  /**
   * Force the number of visible desktop tabs instead of measuring available
   * width. Mainly for tests / stories where layout isn't computed.
   */
  maxVisibleTabs: PropTypes.number,
  className: PropTypes.string,
}

export default AdviceInvestmentTabs
